import { getCurrency } from 'utils/utils';
import { getBounds } from 'utils/flight/flight';
import {
  getAirlines,
  getAirports,
  getBaggage,
  getFlightTypes,
  getStops,
  getStopsInbound,
  getTimes,
} from 'utils/flight/flight-filter-init';
import { FlightSearch } from '../interfaces/FlightSearch';

export interface TimeFilter {
  min: number;
  max: number;
  value: [number, number];
}

export class FlightFilterModel {
  freeText: string;
  stops: {
    number: number;
    label: string;
    checked: boolean;
    minPrice: string;
    disabled?: boolean;
  }[];
  baggage: {
    number: number;
    label: string;
    checked: boolean;
    minPrice: string;
    disabled?: boolean;
  }[];
  price: {
    min: number;
    max: number;
    currencySign: string;
    value: [number, number];
  };
  departureOutboundTime?: TimeFilter;
  arrivalOutboundTime?: TimeFilter;
  departureInboundTime?: TimeFilter;
  arrivalInboundTime?: TimeFilter;
  duration: TimeFilter;
  stopDuration: TimeFilter;
  airlines: {
    code: string;
    label: string;
    checked: boolean;
    minPrice: string;
  }[];
  airports: {
    code: string;
    label: string;
    checked: boolean;
    minPrice: string;
  }[];
  flightTypes: {
    code: string;
    label: string;
    checked: boolean;
    minPrice: string;
  }[];
  isInbound: boolean;
  isMulti: boolean;

  constructor(flights: FlightSearch[], prevFilter?: FlightFilterModel) {
    const currencySign = (flights.length && getCurrency(flights[0]?.priceCurrency)) || '';
    const isOutbound = !!(flights.length && flights[0].outbound);
    this.isInbound = !!(flights.length && flights[0].inbound);
    this.isMulti = !!(flights.length && flights[0].flightBounds?.length);

    // Initialize with previous values if available
    this.freeText = prevFilter?.freeText || '';
    this.stops = this.isInbound
      ? getStopsInbound(flights, currencySign)
      : getStops(flights, currencySign);
    this.baggage = getBaggage(flights, currencySign);
    this.airlines = getAirlines(flights, currencySign);
    this.airports = getAirports(flights, currencySign);
    this.flightTypes = getFlightTypes(flights, currencySign);

    // Preserve checked states from prevFilter if available
    if (prevFilter) {
      this.stops = this.stops.map((stop) => ({
        ...stop,
        checked: prevFilter.stops.find((p) => p.number === stop.number)?.checked || false,
      }));
      this.baggage = this.baggage.map((bag) => ({
        ...bag,
        checked: prevFilter.baggage.find((p) => p.number === bag.number)?.checked || false,
      }));
      this.airlines = this.airlines.map((airline) => ({
        ...airline,
        checked: prevFilter.airlines.find((p) => p.code === airline.code)?.checked || false,
      }));
      this.airports = this.airports.map((airport) => ({
        ...airport,
        checked: prevFilter.airports.find((p) => p.code === airport.code)?.checked || false,
      }));
      this.flightTypes = this.flightTypes.map((type) => ({
        ...type,
        checked: prevFilter.flightTypes.find((p) => p.code === type.code)?.checked || false,
      }));
    }

    if (isOutbound) {
      /// TIME ///
      const departureOutboundTimes = flights.map((flight) => {
        const departureTime = flight.outbound?.departureDateTime;
        return departureTime ? new Date(departureTime).getTime() : 0;
      });

      const arrivalOutboundTimes = flights.map((flight) => {
        const arrivalTime = flight.outbound?.arrivalDateTime;
        return arrivalTime ? new Date(arrivalTime).getTime() : 0;
      });

      this.departureOutboundTime = getTimes(departureOutboundTimes);
      this.arrivalOutboundTime = getTimes(arrivalOutboundTimes);
      if (this.isInbound) {
        const departureInboundTimes = flights.map((flight) => {
          const departureTime = flight.inbound?.departureDateTime;
          return departureTime ? new Date(departureTime).getTime() : 0;
        });

        const arrivalInboundTimes = flights.map((flight) => {
          const arrivalTime = flight.inbound?.arrivalDateTime;
          return arrivalTime ? new Date(arrivalTime).getTime() : 0;
        });
        this.departureInboundTime = getTimes(departureInboundTimes);
        this.arrivalInboundTime = getTimes(arrivalInboundTimes);
      }

      // Preserve time filter values if available
      if (prevFilter) {
        if (this.departureOutboundTime && prevFilter.departureOutboundTime) {
          this.departureOutboundTime.value =
            prevFilter.departureOutboundTime.value[0] !== prevFilter.departureOutboundTime.min ||
            prevFilter.departureOutboundTime.value[1] !== prevFilter.departureOutboundTime.max
              ? prevFilter.departureOutboundTime.value
              : this.departureOutboundTime.value;
        }
        if (this.arrivalOutboundTime && prevFilter.arrivalOutboundTime) {
          this.arrivalOutboundTime.value =
            prevFilter.arrivalOutboundTime.value[0] !== prevFilter.arrivalOutboundTime.min ||
            prevFilter.arrivalOutboundTime.value[1] !== prevFilter.arrivalOutboundTime.max
              ? prevFilter.arrivalOutboundTime.value
              : this.arrivalOutboundTime.value;
        }
        if (this.departureInboundTime && prevFilter.departureInboundTime) {
          this.departureInboundTime.value =
            prevFilter.departureInboundTime.value[0] !== prevFilter.departureInboundTime.min ||
            prevFilter.departureInboundTime.value[1] !== prevFilter.departureInboundTime.max
              ? prevFilter.departureInboundTime.value
              : this.departureInboundTime.value;
        }
        if (this.arrivalInboundTime && prevFilter.arrivalInboundTime) {
          this.arrivalInboundTime.value =
            prevFilter.arrivalInboundTime.value[0] !== prevFilter.arrivalInboundTime.min ||
            prevFilter.arrivalInboundTime.value[1] !== prevFilter.arrivalInboundTime.max
              ? prevFilter.arrivalInboundTime.value
              : this.arrivalInboundTime.value;
        }
      }
    }

    /// PRICE ///
    const prices = flights.map((flight) => flight.flighPrice?.grossAndTaxWithMarkup.value);
    const minPrice = Math.floor(prices.length ? Math.min(...prices) : 0);
    const maxPrice = Math.ceil(prices.length ? Math.max(...prices) : 1);
    this.price = {
      min: minPrice,
      max: maxPrice,
      currencySign,
      value:
        prevFilter?.price?.value &&
        (prevFilter.price.value[0] !== prevFilter.price.min ||
          prevFilter.price.value[1] !== prevFilter.price.max)
          ? prevFilter.price.value
          : [minPrice, maxPrice],
    };

    /// DURATION ///
    const durations = flights
      .reduce((acc: number[], flight) => {
        const bounds = getBounds(flight);
        const durations = bounds.map((bound) => bound.elapsedDTime);
        return [...acc, ...durations];
      }, [])
      .filter((dur) => isFinite(dur));
    const minDuration = Math.floor(durations.length ? Math.min(...durations) : 0);
    const maxDuration = Math.ceil(durations.length ? Math.max(...durations) : 1);
    this.duration = {
      min: minDuration,
      max: maxDuration,
      value:
        prevFilter?.duration?.value &&
        (prevFilter.duration.value[0] !== prevFilter.duration.min ||
          prevFilter.duration.value[1] !== prevFilter.duration.max)
          ? prevFilter.duration.value
          : [minDuration, maxDuration],
    };

    const stopDurations = flights
      .map((flight) => {
        const bounds = getBounds(flight);
        const stopDurations = bounds.map((bound) => {
          return bound.maxConnDTime;
        });

        const maxDur = Math.max(...stopDurations);
        return maxDur;
      })
      .filter((dur) => isFinite(dur));

    const minStopDuration = 0;
    const maxStopDuration = Math.ceil(stopDurations.length ? Math.max(...stopDurations) : 1);
    this.stopDuration = {
      min: minStopDuration,
      max: maxStopDuration,
      value:
        prevFilter?.stopDuration?.value &&
        (prevFilter.stopDuration.value[0] !== prevFilter.stopDuration.min ||
          prevFilter.stopDuration.value[1] !== prevFilter.stopDuration.max)
          ? prevFilter.stopDuration.value
          : [minStopDuration, maxStopDuration],
    };
  }
}
